import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  TextInput, Grid, Select,
  Box,
  Text,
  Button,
  Center,
} from '@mantine/core';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { notifications } from '@mantine/notifications';
import {
  DateInput,
  Modal,
  Page,
  ProcessHeader,
} from '@/components';
import validate from '@/helpers/validations';
import routes from '@/routes';
import { routeWithParams } from '@/helpers/routes';
import { getMutationResult } from '@/helpers/query';
import {
  useSaveMutation,
  useLoanOptionsMutation,
  useNewOfferMutation,
  useSendApproveLinkToClientMutation,
} from '@/services/api';
import { updateProposal, updateExtraDetails, updateBorrower } from '@/data/features/proposal/proposal';
import { updateLoanOptions } from '@/data/features/loanOptions/loanOptions';
import { stringToDate, toISOString } from '@/helpers/date';
import ProcessButton from '@/components/ProcessButton';
import ClientIcon from '@/assets/images/clientIcon.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './borrower.module.css';

const Borrower = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [canCloseModal, setCanCloseModal] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(15);
  const [apiCreateOrUpdateOffer] = useSaveMutation();
  const [apiCgForLoanOptions] = useLoanOptionsMutation();
  const [apiSendApproveLinkToClient] = useSendApproveLinkToClientMutation();
  const [apiNewOffer] = useNewOfferMutation();
  const dispatch = useDispatch();
  const { id: paramsId } = useParams();
  const [searchParams] = useSearchParams();
  const dontReset = searchParams.get('dontReset') === 'true';

  const { borrower, extraDetails, id: processId } = useSelector((state) => state.proposal);
  const { numberOfBorrowers: initialNumberOfBorowers, isApprovedClient } = extraDetails;

  const [localNumberOfBorrowers, setLocalNumberOfBorrowers] = useState(initialNumberOfBorowers?.toString() || '1');

  const EMPTY_BORROWER = {
    fullName: '',
    idNumber: '',
    phoneNumber: '',
    email: '',
    city: '',
    address: '',
  };

  const mainBorrower = borrower[0] || {};

  const required = t('globals.validations.required');
  const hebrew = t('globals.validations.hebrew');
  const numbers = t('globals.validations.number');
  const phone = t('globals.validations.phone');
  const date = t('globals.validations.date');
  const email = t('globals.validations.email');
  const uid = t('globals.validations.uid');
  const isNotFutureDate = t('globals.validations.futureDate');
  const isAtLeast18YearsOld = t('globals.validations.atLeast18years');
  const minLength = t('globals.validations.minLength', { count: 2 });

  const formValidations = {
    fullName: (value) => validate(value, { required, hebrew, minLength }),
    phoneNumber: (value) => validate(value, { required, numbers, mobile: phone }),
    email: (value) => validate(value, { required, email }),
    idNumber: (value) => validate(value, { required, uid }),
    numberOfBorrowers: (value) => validate(value, { required, numbers }),
    youngestBorrowerDOB: (value) => validate(value, {
      required, date, isNotFutureDate, isAtLeast18YearsOld,
    }),
    oldestBorrowerDOB: (value) => {
      if (form.getValues().numberOfBorrowers !== '1') {
        const baseValidation = validate(value, {
          required, date, isNotFutureDate, isAtLeast18YearsOld,
        });
        if (baseValidation) {
          return baseValidation;
        }
        const youngest = form.getValues().youngestBorrowerDOB;
        if (youngest && value) {
          const youngestDate = stringToDate(youngest);
          const oldestDate = stringToDate(value);

          if (oldestDate > youngestDate) {
            return 'יש להזין תאריך תקין';
          }
        }
      }
      return null;
    },
  };

  const getInitialValues = () => {
    if (paramsId) {
      if (extraDetails?.numberOfBorrowers === 1) {
        return { ...mainBorrower, ...extraDetails, oldestBorrowerDOB: null };
      }
      return { ...mainBorrower, ...extraDetails };
    }
    if (dontReset) {
      return { ...borrower[0], numberOfBorrowers: '1' };
    }
    return { ...EMPTY_BORROWER, numberOfBorrowers: '1' };
  };

  const form = useForm({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    initialValues: getInitialValues(),
    validate: formValidations,
  });

  useEffect(() => {
    if (!paramsId && !dontReset) {
      dispatch({ type: 'RESET_ALL' });
    }
  }, [paramsId, dontReset]);

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate form
      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }
      // Save data and navigate to next step

      const {
        fullName,
        phoneNumber,
        email: borrowerEmail,
        idNumber,
        oldestBorrowerDOB,
        youngestBorrowerDOB,
      } = form.getValues();

      const oldestDOB = oldestBorrowerDOB || youngestBorrowerDOB;

      const youngestDateAsString = toISOString(youngestBorrowerDOB);
      const oldestDateAsString = toISOString(oldestDOB);

      const borrowers = Array.from({ length: numberOfBorrowers }, (_, i) => {
        if (i === 0) {
          return {
            ...borrower[0],
            fullName,
            phoneNumber,
            email: borrowerEmail,
            idNumber,
          };
        }
        const existing = borrower?.[i] || {};
        return { ...EMPTY_BORROWER, ...existing };
      });

      const formattedBorrowers = borrowers.map((b) => ({
        ...b,
      }));

      const objToSendToNewOffer = {
        offerId: processId,
        borrower: formattedBorrowers,
      };

      const response = await apiNewOffer(objToSendToNewOffer);
      const { data: { data: responseData } } = response;

      if (response?.data?.status === 202) {
        dispatch(updateExtraDetails({ isApprovedClient: false, clientId: responseData?.clientId || '' }));
        setShowModal(true);

        const sendApproveLinkObj = {
          phoneNumber,
          fullName,
          email: borrowerEmail,
          clientId: responseData?.clientId || '',
        };

        const { isError: sendToClientError } = await getMutationResult(
          sendApproveLinkObj,
          apiSendApproveLinkToClient,
        );

        if (sendToClientError) {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }

        setLoading(false);
        return;
      }

      const sendValues = {
        borrower: formattedBorrowers,
        numberOfBorrowers,
        oldestBorrowerDOB: oldestDateAsString,
        youngestBorrowerDOB: youngestDateAsString,
        id: responseData?.offerId || processId,
        isRecycling: false,
      };

      // Send data via API
      const {
        data,
        isError,
      } = await getMutationResult(sendValues, apiCreateOrUpdateOffer);

      const getLoanOptionsObj = {
        oldestBorrowerDOB: oldestDateAsString,
        youngestBorrowerDOB: youngestDateAsString,
      };

      if (!isError) {
        const { data: id } = data;
        const {
          data: loanOptionData,
          isError: loanOptionIsError,
        } = await getMutationResult(getLoanOptionsObj, apiCgForLoanOptions);

        if (!loanOptionIsError) {
          const { data: loans } = loanOptionData;
          dispatch(updateLoanOptions(loans));
          dispatch(updateProposal({ id }));
          dispatch(updateBorrower(borrowers));
          dispatch(updateExtraDetails({
            youngestBorrowerDOB: form.getValues().youngestBorrowerDOB,
            oldestBorrowerDOB: oldestDOB,
            numberOfBorrowers,
            isRecycling: false,
          }));
          // Navigate to next step
          navigate(routeWithParams(routes.newProposal.pages.find((p) => p.id === 'reason').path, { id }));
        } else {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }
      } else {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isApprovedClient && !processId) {
      setShowModal(false);
      handleContinue();
    }
  }, [isApprovedClient]);

  useEffect(() => {
    if (!showModal) return;

    setCanCloseModal(false);
    setSecondsLeft(15);

    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          setCanCloseModal(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showModal]);

  const numberOfBorrowers = parseInt(localNumberOfBorrowers, 10);

  // Sync form with local state
  useEffect(() => {
    form.setFieldValue('numberOfBorrowers', localNumberOfBorrowers);
  }, [localNumberOfBorrowers, form]);

  return (
    <ProcessHeader
      headerText="פרטי הלקוח"
      headerColor="#E0EDFF"
      headerTextColor="#3348FF"
      subtitle="יש למלא את פרטי הלקוח ולציין את מספר הלווים שיהיו בהלוואה"
      infoText="הלוואת משכנתא ניתנת ללווה אחד ועד שלושה לווים. להמשך התהליך יש לציין את תאריך הלידה של הלווים. במקרה בו בהלוואה יהיו שלושה לווים יש לציין את תאריך הלידה של הלווה הצעיר ביותר ושל הלווה המבוגר ביותר."
      icon={ClientIcon}
    >

      <Page.Section>
        <div className={classes.formContainer}>
          <Grid className={classes.formGrid} style={{ width: '100%', maxWidth: '500px' }}>
            {/* Client Name */}
            <Grid.Col span={6}>
              <TextInput
                label="שם מלא של הלקוח"
                required
                {...form.getInputProps('fullName')}
              />
            </Grid.Col>

            {/* Phone Number */}
            <Grid.Col span={6}>
              <TextInput
                label="מספר טלפון"
                required
                maxLength={10}
                {...form.getInputProps('phoneNumber')}
              />
            </Grid.Col>

            {/* Email */}
            <Grid.Col span={6}>
              <TextInput
                label="כתובת אימייל"
                required
                {...form.getInputProps('email')}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput
                label="מספר ת.ז"
                required
                {...form.getInputProps('idNumber')}
              />
            </Grid.Col>

            {/* Number of Borrowers */}
            <Grid.Col span={6}>
              <Select
                label="מספר הלווים בהלוואה"
                placeholder="בחרו מספר לווים"
                data={[
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                ]}
                required
                value={localNumberOfBorrowers}
                onChange={(value) => {
                  setLocalNumberOfBorrowers(value);
                  form.setFieldValue('numberOfBorrowers', value);
                  if (value === '1') {
                    form.setFieldValue('oldestBorrowerDOB', null);
                  }
                }}
              />
            </Grid.Col>

            {/* main Borrower DOB */}
            {numberOfBorrowers === 1 && (
              <Grid.Col span={6}>
                <DateInput
                  label="תאריך לידה"
                  placeholder="בחרו תאריך לידה"
                  maxDate={new Date()}
                  required
                  form={{ ...form.getInputProps('youngestBorrowerDOB') }}
                />
              </Grid.Col>
            )}

            {/* Youngest and oldest Borrower DOB - only show if multiple borrowers */}
            {numberOfBorrowers > 1 && (
              <>
                {/* Youngest Borrower DOB */}
                <Grid.Col span={6}>
                  <DateInput
                    label="תאריך לידה של הלווה הצעיר ביותר"
                    placeholder="בחרו תאריך לידה"
                    maxDate={new Date()}
                    required
                    form={{ ...form.getInputProps('youngestBorrowerDOB') }}
                  />
                </Grid.Col>

                {/* Oldest Borrower DOB */}
                <Grid.Col span={6}>
                  <DateInput
                    label="תאריך לידה של הלווה המבוגר ביותר"
                    placeholder="בחרו תאריך לידה"
                    maxDate={new Date()}
                    required
                    form={{ ...form.getInputProps('oldestBorrowerDOB') }}
                  />
                </Grid.Col>

              </>)}

            {/* Continue Button */}
            <Grid.Col span={12}>
              <ProcessButton onClick={handleContinue} loading={loading} />
            </Grid.Col>
          </Grid>
        </div>
      </Page.Section>

      {/* Bottom Image */}
      <div className={classes.bottomImageContainer}>
        {/* <img
          src="/src/assets/images/borrowerPageImage.svg"
          alt="Borrower page illustration"
          className={classes.bottomImage}
        /> */}
      </div>

      <Modal
        opened={showModal}
        showClose={false}
        size='md'
      >
        <Center p={10}>
          <Box className={classes.modalHeader}>
            <Text fz={15}><strong>אישור הלקוח/ה נדרש להמשך התהליך</strong></Text>
          </Box>
        </Center>
        <Text fz={15} p={10}>הודעה באימייל וב-SMS
          נשלחה ללקוח/ה ובה הנחיות לאישור מדיניות הפרטיות שלנו הנדרש להמשך התהליך.
        </Text>
        <Text p={10} fz={18}>יש להמתין לאישור הלקוח, ולאחר אישורו תועבר/י להמשך התהליך.</Text>
        <Text p={10} fz={15}>
          <strong>אם תבחר/י לצאת מהתהליך, </strong>
          תועבר/י למסך הבית, ולאחר אישור הלקוח ניתן להתחיל את התהליך מחדש על
          ידי לחיצה על יצירת הצעה חדשה והזנת פרטי הלקוח מחדש.
        </Text>
        <Center p={10}>
          <Button
            disabled={!canCloseModal}
            onClick={() => { navigate('/'); }}
          >
            {canCloseModal ? 'סגירה וחזרה למסך הבית' : `אפשר לסגור בעוד ${secondsLeft} שניות`}
          </Button>
        </Center>
      </Modal>

    </ProcessHeader>
  );
};

export default Borrower;
